// Tenant commercial layer (packaging Phases 3): TRIALS (temporary TRIAL grants
// with a real expiry — never a plan mutation) and OVERRIDES (negotiated
// exceptions: grant / deny / limit_override, reason-required, effective-dated,
// contract-attributable). Writes need product:entitlement:manage; everything
// lands in the entitlement audit trail + provenance log.
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import * as svc from '../../services/tenantService';
import { useConfirmWithReason } from '../../components/console';

const { Text } = Typography;

export const TenantCommercial: React.FC<{ tenantId: string; canManage: boolean }> = ({ tenantId, canManage }) => {
  const [overrides, setOverrides] = useState<svc.EntitlementOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [trialDays, setTrialDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirmWithReason();
  const [showCreate, setShowCreate] = useState(false);
  const [ov, setOv] = useState<{ productCode: string; kind: string; limitsText: string; effectiveTo?: string; reason: string; contractRef?: string }>({ productCode: '', kind: 'grant', limitsText: '', reason: '' });
  const [catalog, setCatalog] = useState<Array<{ code: string; name: string }>>([]);

  const load = () => {
    setLoading(true);
    svc.listOverrides(tenantId).then(setOverrides).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenantId]);
  useEffect(() => {
    svc.listCatalog().then((r: any) => {
      const items = (r?.products ?? r ?? []) as any[];
      setCatalog(items.map((p) => ({ code: p.code ?? p.productCode, name: p.name ?? p.code })));
    }).catch(() => {});
  }, []);

  const startTrial = async () => {
    setBusy(true);
    try {
      const r = await svc.startTrial(tenantId, { days: trialDays });
      message.success(`Trial started — ${r.granted?.length ?? 0} product(s) until ${String(r.trialEndsAt).slice(0, 10)}`);
    } catch (e: any) { message.error(e?.response?.data?.message || 'Trial start failed'); }
    finally { setBusy(false); }
  };
  const extendTrial = async () => {
    const reason = await confirm({ title: 'Extend trial', message: 'Extend this tenant’s trial by 7 days?', okText: 'Extend', reasonRequired: true, reasonLabel: 'Reason for extending the trial' });
    if (!reason) return;
    setBusy(true);
    try {
      const r = await svc.extendTrial(tenantId, { days: 7, reason });
      message.success(`Trial extended to ${String(r.trialEndsAt).slice(0, 10)}`);
    } catch (e: any) { message.error(e?.response?.data?.message || 'Extend failed'); }
    finally { setBusy(false); }
  };

  const createOverride = async () => {
    if (!ov.productCode || !ov.reason.trim()) { message.error('Product and a commercial reason are required'); return; }
    let limits: Record<string, unknown> | undefined;
    if (ov.limitsText.trim()) {
      try { limits = JSON.parse(ov.limitsText); } catch { message.error('Limits is not valid JSON'); return; }
    }
    setBusy(true);
    try {
      await svc.createOverride(tenantId, {
        productCode: ov.productCode, kind: ov.kind, limits,
        effectiveTo: ov.effectiveTo || undefined, reason: ov.reason, contractRef: ov.contractRef || undefined,
      });
      message.success('Override recorded and applied');
      setShowCreate(false);
      setOv({ productCode: '', kind: 'grant', limitsText: '', reason: '' });
      load();
    } catch (e: any) { message.error(e?.response?.data?.message || 'Override failed'); }
    finally { setBusy(false); }
  };

  const revoke = async (row: svc.EntitlementOverride) => {
    const reason = await confirm({ title: 'Revoke override', message: `Revoke the ${row.productCode ?? ''} override?`, okText: 'Revoke', danger: true, reasonRequired: true, reasonLabel: 'Reason for revoking this override' });
    if (!reason) return;
    try { await svc.revokeOverride(tenantId, row.id, reason); message.success('Revoked'); load(); }
    catch (e: any) { message.error(e?.response?.data?.message || 'Revoke failed'); }
  };

  return (
    <Card title="Commercial: trial & agreement overrides" size="small" style={{ marginTop: 16 }}>
      {dialog}
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Trials are a TEMPORARY layer on top of the permanent subscription — expiry reconciles back automatically and never deletes data. Overrides are negotiated exceptions (contract-attributable), not normal add-ons."
      />
      {canManage && (
        <Space wrap style={{ marginBottom: 12 }}>
          <InputNumber min={1} max={365} value={trialDays} onChange={(v) => setTrialDays(Number(v) || 30)} addonAfter="days" style={{ width: 140 }} />
          <Popconfirm title={`Start a ${trialDays}-day full-access trial?`} onConfirm={startTrial}>
            <Button type="primary" loading={busy}>Start trial</Button>
          </Popconfirm>
          <Button onClick={extendTrial} loading={busy}>Extend trial +7d</Button>
          <Button onClick={() => setShowCreate(true)}>New override…</Button>
        </Space>
      )}
      <Table
        rowKey="id" size="small" loading={loading} dataSource={overrides} pagination={false}
        locale={{ emptyText: 'No commercial overrides' }}
        columns={[
          { title: 'Product', dataIndex: 'productCode', render: (c: string) => <Text code>{c}</Text> },
          { title: 'Kind', dataIndex: 'kind', render: (k: string) => <Tag color={k === 'deny' ? 'red' : k === 'limit_override' ? 'orange' : 'green'}>{k}</Tag> },
          { title: 'Limits', dataIndex: 'limits', render: (l: any) => (l && Object.keys(l).length ? <Text code>{JSON.stringify(l)}</Text> : '—') },
          { title: 'Reason', dataIndex: 'reason', ellipsis: true },
          { title: 'Contract', dataIndex: 'contractRef', render: (v: string) => v || '—' },
          { title: 'Window', key: 'w', render: (_: any, r: svc.EntitlementOverride) => `${r.effectiveFrom.slice(0, 10)} → ${r.effectiveTo ? r.effectiveTo.slice(0, 10) : 'open'}` },
          { title: 'Status', key: 's', render: (_: any, r: svc.EntitlementOverride) => (r.revokedAt ? <Tag>revoked</Tag> : <Tag color="blue">active</Tag>) },
          {
            title: '', key: 'a', render: (_: any, r: svc.EntitlementOverride) =>
              canManage && !r.revokedAt ? <Button size="small" type="link" danger onClick={() => revoke(r)}>Revoke</Button> : null,
          },
        ]}
      />
      <Modal title="New commercial override" open={showCreate} onOk={createOverride} onCancel={() => setShowCreate(false)} okText="Record & apply" confirmLoading={busy}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            showSearch placeholder="Product" style={{ width: '100%' }} optionFilterProp="label"
            value={ov.productCode || undefined}
            options={catalog.map((p) => ({ value: p.code, label: `${p.name} (${p.code})` }))}
            onChange={(v) => setOv((o) => ({ ...o, productCode: v }))}
          />
          <Select
            style={{ width: '100%' }} value={ov.kind}
            options={[
              { value: 'grant', label: 'grant — include this product beyond the plan' },
              { value: 'deny', label: 'deny — exclude it even if the plan includes it' },
              { value: 'limit_override', label: 'limit_override — change a limit value' },
            ]}
            onChange={(v) => setOv((o) => ({ ...o, kind: v }))}
          />
          <Input placeholder='Limits JSON (optional) e.g. {"integrations.max":5}' value={ov.limitsText} onChange={(e) => setOv((o) => ({ ...o, limitsText: e.target.value }))} />
          <Input type="date" value={ov.effectiveTo || ''} onChange={(e) => setOv((o) => ({ ...o, effectiveTo: e.target.value }))} placeholder="Effective until (optional)" />
          <Input placeholder="Commercial reason (required)" value={ov.reason} onChange={(e) => setOv((o) => ({ ...o, reason: e.target.value }))} />
          <Input placeholder="Contract reference (optional)" value={ov.contractRef || ''} onChange={(e) => setOv((o) => ({ ...o, contractRef: e.target.value }))} />
        </Space>
      </Modal>
    </Card>
  );
};

export default TenantCommercial;
